import { useEffect, type ReactNode } from 'react'
import { useDropzone } from 'react-dropzone'
import { blockTheme, hsl } from '@teach/ui'
import type { DesignImage } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { setLocalizedField } from '@/features/editor/lib/localizedField'
import { IMAGE_EXTS } from '@/features/editor/lib/imageFormats'
import { onImageReplaceRequest } from '@/features/editor/lib/designImageReplace'
import { useImageDrop } from '@/features/editor/components/shared/useImageDrop'
import { UploadProgressOverlay } from '@/features/editor/components/shared/UploadProgressOverlay'
import { useDesignImageTarget } from '../../design/useDesignImageTarget'

interface Props {
  element: DesignImage
  contentLang: ContentLang
  url: string
  onUpdate: (patch: Partial<DesignImage>) => void
  children: ReactNode
}

/**
 * Уже загруженная картинка на холсте в правке: на неё можно перетащить другой файл,
 * а «Заменить» из быстрого тулбара открывает выбор файла. Путь загрузки тот же,
 * что у панели (`useImageDrop`): очередь, цель догрузки, URL в черновик.
 */
export const CanvasImageFilled = ({ element, contentLang, url, onUpdate, children }: Props) => {
  const target = useDesignImageTarget(element, contentLang)
  const { onDrop, progress } = useImageDrop({
    value: url,
    onChange: (next) => onUpdate(setLocalizedField('imageUrl', contentLang, next ?? '')),
    compressionType: 'banner_compression',
    disabled: false,
    blockTarget: target,
  })
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': IMAGE_EXTS },
    multiple: false,
    // Клик по картинке выделяет элемент — окно файла открывает только «Заменить».
    noClick: true,
    noKeyboard: true,
  })
  useEffect(
    () => onImageReplaceRequest((id) => (id === element.id ? open() : undefined)),
    [element.id, open],
  )

  return (
    <div {...getRootProps()} className="relative w-full outline-none">
      <input {...getInputProps()} />
      {children}
      <UploadProgressOverlay progress={progress} tone="canvas" />
      {isDragActive ? (
        // Тащат файл над картинкой — подсветка акцентом: отпусти, и она заменится.
        <div
          className="pointer-events-none absolute inset-0 rounded-lg"
          style={{ boxShadow: `inset 0 0 0 3px ${hsl(blockTheme.primary)}` }}
        />
      ) : null}
    </div>
  )
}
